import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetTokenOperationsQuery, useGetTokenOperationsActionsQuery } from "../store/api";
import {
    TokenOperationFilters,
    TokenOperationItem,
} from "../types/tokenOperations";
import { format } from "date-fns";

const defaultFilters: TokenOperationFilters = {
    sort_by: "created_at",
    sort_dir: "desc",
    per_page: 20,
};

const TokenOperationsPage: React.FC = () => {
    const { t } = useTranslation("common");
    const [filters, setFilters] = useState<TokenOperationFilters>(
        defaultFilters
    );

    const { data, isLoading } = useGetTokenOperationsQuery(filters);
    const { data: actionsData } = useGetTokenOperationsActionsQuery();

    const handleInput =
        (field: keyof TokenOperationFilters) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const value = e.target.value;
            setFilters((prev) => ({
                ...prev,
                [field]:
                    field === "user_id" || field === "tournament_id"
                        ? Number(value) || undefined
                        : value || undefined,
            }));
        };

    const handleSort = (field: "created_at" | "amount" | "user_id") => {
        setFilters((prev) => ({
            ...prev,
            sort_by: field,
            sort_dir:
                prev.sort_by === field && prev.sort_dir === "asc"
                    ? "desc"
                    : "asc",
        }));
    };

    const rows = useMemo(() => data?.data ?? [], [data]);

    return (
        <div className="p-6">
            <div className="flex flex-col gap-4 mb-4">
                <h1 className="text-2xl font-semibold">Игровые операции</h1>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <input
                        type="number"
                        className="input"
                        placeholder="User ID"
                        value={filters.user_id ?? ""}
                        onChange={handleInput("user_id")}
                    />
                    <input
                        type="number"
                        className="input"
                        placeholder="Tournament ID"
                        value={filters.tournament_id ?? ""}
                        onChange={handleInput("tournament_id")}
                    />
                    <select
                        className="input"
                        value={filters.action ?? ""}
                        onChange={handleInput("action")}
                    >
                        <option value="">Действие (Action)</option>
                        {actionsData?.map((action) => (
                            <option key={action} value={action}>
                                {action}
                            </option>
                        ))}
                    </select>
                    <select
                        className="input"
                        value={filters.direction ?? ""}
                        onChange={handleInput("direction")}
                    >
                        <option value="">Направление</option>
                        <option value="debit">Дебет</option>
                        <option value="credit">Кредит</option>
                    </select>
                    <input
                        className="input"
                        placeholder="Source"
                        value={filters.source ?? ""}
                        onChange={handleInput("source")}
                    />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <input
                        className="input"
                        placeholder="Reference ID"
                        value={filters.reference_id ?? ""}
                        onChange={handleInput("reference_id")}
                    />
                    <input
                        className="input"
                        placeholder="Operation ID"
                        value={filters.operation_id ?? ""}
                        onChange={handleInput("operation_id")}
                    />
                    <input
                        type="date"
                        className="input"
                        value={filters.date_from ?? ""}
                        onChange={handleInput("date_from")}
                    />
                    <input
                        type="date"
                        className="input"
                        value={filters.date_to ?? ""}
                        onChange={handleInput("date_to")}
                    />
                    <select
                        className="input"
                        value={filters.per_page ?? 20}
                        onChange={handleInput("per_page")}
                    >
                        {[20, 50, 100].map((n) => (
                            <option key={n} value={n}>
                                {n} / page
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="overflow-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 cursor-pointer text-left" onClick={() => handleSort("created_at")}>
                                Дата
                            </th>
                            <th className="px-4 py-2">User</th>
                            <th className="px-4 py-2">Tournament</th>
                            <th className="px-4 py-2">Action</th>
                            <th className="px-4 py-2">Direction</th>
                            <th className="px-4 py-2 cursor-pointer text-left" onClick={() => handleSort("amount")}>
                                Amount
                            </th>
                            <th className="px-4 py-2">Source</th>
                            <th className="px-4 py-2">Reference</th>
                            <th className="px-4 py-2">Op ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {isLoading && (
                            <tr>
                                <td colSpan={9} className="px-4 py-4 text-center">
                                    {t("loading", { defaultValue: "Loading..." })}
                                </td>
                            </tr>
                        )}
                        {!isLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-4 py-4 text-center">
                                    Нет данных
                                </td>
                            </tr>
                        )}
                        {rows.map((item: TokenOperationItem) => (
                            <tr key={item.id}>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    {format(new Date(item.created_at), "yyyy-MM-dd HH:mm")}
                                </td>
                                <td className="px-4 py-2">
                                    <div className="font-medium">#{item.user_id}</div>
                                    <div className="text-gray-500 text-xs">
                                        {item.user?.name} {item.user?.telegram_username ? `(@${item.user.telegram_username})` : ""}
                                    </div>
                                </td>
                                <td className="px-4 py-2">
                                    {item.tournament_id ? `#${item.tournament_id}` : "-"}
                                </td>
                                <td className="px-4 py-2 font-medium">{item.action || "-"}</td>
                                <td className="px-4 py-2">{item.direction}</td>
                                <td className="px-4 py-2">
                                    <div>{item.amount}</div>
                                    <div className="text-gray-500 text-xs">
                                        {item.balance_before ?? "-"} → {item.balance_after ?? "-"}
                                    </div>
                                </td>
                                <td className="px-4 py-2">{item.source || "-"}</td>
                                <td className="px-4 py-2">{item.reference_id || "-"}</td>
                                <td className="px-4 py-2 break-all text-xs">{item.operation_id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {data?.meta && (
                <div className="flex items-center justify-between mt-4 text-sm">
                    <div>
                        Стр. {data.meta.current_page} из {data.meta.last_page} — всего {data.meta.total}
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="btn-secondary px-3 py-1"
                            disabled={!data.links.prev}
                            onClick={() =>
                                setFilters((prev) => ({
                                    ...prev,
                                    page: Math.max((prev.page ?? data.meta.current_page) - 1, 1),
                                }))
                            }
                        >
                            ←
                        </button>
                        <button
                            className="btn-secondary px-3 py-1"
                            disabled={!data.links.next}
                            onClick={() =>
                                setFilters((prev) => ({
                                    ...prev,
                                    page: (prev.page ?? data.meta.current_page) + 1,
                                }))
                            }
                        >
                            →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TokenOperationsPage;

